import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface UserLocation {
   userId: string;
   latitude: number;
   longitude: number;
   address: string;
   city?: string;
   country?: string;
   placeId?: string;
   createdAt?: any;
   updatedAt?: any;
}

class LocationService {
   /**
    * Check if user has location stored
    */
   async getUserLocation(userId: string): Promise<UserLocation | null> {
      try {
         const locationDoc = await getDoc(doc(db, 'locations', userId));
         if (locationDoc.exists()) {
            return locationDoc.data() as UserLocation;
         }
         return null;
      } catch (error) {
         console.error('Error fetching user location:', error);
         return null;
      }
   }

   /**
    * Save user location to Firestore
    */
   async saveUserLocation(
      locationData: Omit<UserLocation, 'createdAt' | 'updatedAt'>
   ): Promise<void> {
      try {
         const locationRef = doc(db, 'locations', locationData.userId);
         const existingLocation = await getDoc(locationRef);

         if (existingLocation.exists()) {
            // Update existing location
            await setDoc(
               locationRef,
               {
                  ...locationData,
                  updatedAt: serverTimestamp(),
               },
               { merge: true }
            );
         } else {
            // Create new location
            await setDoc(locationRef, {
               ...locationData,
               createdAt: serverTimestamp(),
               updatedAt: serverTimestamp(),
            });
         }
      } catch (error) {
         console.error('Error saving user location:', error);
         throw error;
      }
   }

   /**
    * Get user's current position using browser geolocation
    */
   getCurrentPosition(): Promise<GeolocationPosition> {
      return new Promise((resolve, reject) => {
         if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported by this browser'));
            return;
         }

         navigator.geolocation.getCurrentPosition(
            (position) => resolve(position),
            (error) => reject(error),
            {
               enableHighAccuracy: true,
               timeout: 10000,
               maximumAge: 600000, // 10 minutes
            }
         );
      });
   }

   /**
    * Get address from coordinates using Google Places API
    */
   async getAddressFromCoordinates(
      lat: number,
      lng: number
   ): Promise<{
      address: string;
      city?: string;
      country?: string;
      placeId?: string;
   }> {
      return new Promise((resolve, reject) => {
         if (!window.google) {
            reject(new Error('Google Maps API not loaded'));
            return;
         }

         const geocoder = new window.google.maps.Geocoder();

         geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
            if (status === 'OK' && results && results[0]) {
               const result = results[0];
               let city = '';
               let country = '';

               // Extract city and country from address components
               for (const component of result.address_components) {
                  if (component.types.includes('locality')) {
                     city = component.long_name;
                  } else if (component.types.includes('administrative_area_level_1') && !city) {
                     city = component.long_name;
                  } else if (component.types.includes('country')) {
                     country = component.long_name;
                  }
               }

               resolve({
                  address: result.formatted_address,
                  city,
                  country,
                  placeId: result.place_id,
               });
            } else {
               reject(new Error('Geocoding failed: ' + status));
            }
         });
      });
   }

   /**
    * Request user location and save it
    */
   async requestAndSaveUserLocation(userId: string): Promise<UserLocation> {
      try {
         // Get current position
         const position = await this.getCurrentPosition();
         const { latitude, longitude } = position.coords;

         // Get address from coordinates
         const addressInfo = await this.getAddressFromCoordinates(latitude, longitude);

         // Create location data
         const locationData: Omit<UserLocation, 'createdAt' | 'updatedAt'> = {
            userId,
            latitude,
            longitude,
            address: addressInfo.address,
            city: addressInfo.city,
            country: addressInfo.country,
            placeId: addressInfo.placeId,
         };

         // Save to Firestore
         await this.saveUserLocation(locationData);

         return {
            ...locationData,
            createdAt: new Date(),
            updatedAt: new Date(),
         };
      } catch (error) {
         console.error('Error requesting and saving user location:', error);
         throw error;
      }
   }
}

export const locationService = new LocationService();
export default locationService;
